'use client'

import { useEffect, useState } from 'react'
import { useLocaleStore } from '@/stores/locale-store'

const DEFAULT_LOCALE = 'de'
const LOCAL_STORAGE_KEY = 'trusty-locale'

export type Locale = 'de' | 'en'

const getBrowserLocale = (): Locale => {
  if (typeof navigator === 'undefined') return DEFAULT_LOCALE
  const browserLang = navigator.language
  if (browserLang && browserLang.trim().length < 2) return DEFAULT_LOCALE
  const strippedLang = browserLang.trim().split('-')[0]

  if (strippedLang.startsWith('en')) return 'en'
  if (strippedLang.startsWith('de')) return 'de'
  return DEFAULT_LOCALE
}

const getDefaultLocale = (): Locale => {
  const browserLocale = getBrowserLocale()
  const storedLocale = typeof localStorage !== 'undefined' ? localStorage.getItem(LOCAL_STORAGE_KEY) : null
  return (storedLocale || browserLocale) as Locale
}

export const useLocalisation = () => {
  const [defaultLocale, setLocaleIntern] = useState(getDefaultLocale())
  const setAppStateLocale = useLocaleStore((state) => state.setLocale)

  const setLocale = (lang: Locale) => {
    typeof localStorage !== 'undefined' && localStorage.setItem(LOCAL_STORAGE_KEY, lang)
    setLocaleIntern(lang)
    setAppStateLocale?.(lang)
  }

  useEffect(() => {
    setAppStateLocale?.(defaultLocale)
  }, [defaultLocale, setAppStateLocale])

  return {
    defaultLocale: defaultLocale,
    setLocale,
  }
}
